import React, { useCallback, useEffect, useState } from "react";
import { isDateOnlyBefore, toIsoDate } from "@common/helpers/date";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { Button } from "@design-system/Button/Button";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { Field, pickFieldControlAriaProps } from "@design-system/Field/Field";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import {
    clampToMinDateOnly,
    getFirstUnlockedDateOnly,
    isLockedByDateOnly,
    laterDateOnly,
} from "@helpers/lock";
import styles from "./ConfirmInvoiceStatusDateModal.module.scss";

interface ConfirmInvoiceStatusDateModalProps {
    show: boolean;
    title: string;
    description?: string;
    dateFieldLabel?: string;
    question?: React.ReactNode;
    dateLabel?: string;
    // Confirm button copy. Defaults to "Confirm"; the revise flow overrides it.
    confirmLabel?: string;
    // Disables both buttons while an async confirm action is in flight.
    isProcessing?: boolean;
    /**
     * False while the entity period lock query is still loading. Until it
     * resolves the lock date is unknown, so Confirm stays disabled — otherwise
     * a date inside a locked period could be submitted during that window,
     * bypassing the (FE-only) lock guard the backend does not enforce.
     */
    isLockResolved?: boolean;
    /**
     * Native invoices only: earliest selectable date (the invoice date),
     * so the picker disables everything before it (same day is allowed).
     */
    minDate?: string;
    /**
     * Native invoices only: the invoice date, used for the inline error
     * backstop message. Undefined/null for non-native invoices.
     */
    invoiceDate?: string | null;
    /**
     * The relevant entity's (or, for a bulk selection, the latest across the
     * selected entities') period lock date — see `getLatestLockDate`. The
     * picker disables every date on or before it, on top of the invoice-date
     * floor, so a void/uncollectible date can never land in a locked period.
     * FE-only guard, same as `useIsInvoiceLocked`; backend enforcement is a
     * follow-up.
     */
    lockDate?: Date | null;
    onCancel: () => void;
    onConfirm: (date: string) => void;
}

export const ConfirmInvoiceStatusDateModal: React.FC<
    ConfirmInvoiceStatusDateModalProps
> = ({
    show,
    title,
    description,
    dateFieldLabel,
    question,
    dateLabel,
    confirmLabel = "Confirm",
    isProcessing = false,
    isLockResolved = true,
    minDate,
    invoiceDate,
    lockDate,
    onCancel,
    onConfirm,
}) => {
    // Combine the invoice-date floor with the period-lock floor: whichever is
    // later wins, since both are "may not precede" constraints on the same
    // field. Locked dates aren't just the caller's problem to avoid — they're
    // guarded here too, exactly like the invoice-date check below.
    const firstUnlockedDate = getFirstUnlockedDateOnly(lockDate ?? null);
    const effectiveMinDate = laterDateOnly(minDate, firstUnlockedDate);

    // Today, unless that lands before the earliest selectable date (future-
    // dated invoice, or a period lock covering today): then start at the
    // earliest selectable date so the modal never opens with a red error and
    // a disabled Confirm.
    const getInitialDate = useCallback(
        () => clampToMinDateOnly(toIsoDate(new Date()), effectiveMinDate),
        [effectiveMinDate],
    );

    const [date, setDate] = useState<string>(getInitialDate);

    const dateError =
        (invoiceDate && isDateOnlyBefore(date, invoiceDate)
            ? "Date must be on or after the invoice date"
            : undefined) ??
        (isLockedByDateOnly(lockDate ?? null, date)
            ? "Date falls within a locked accounting period"
            : undefined);

    // Both modal instances stay mounted and are only toggled via `show`, so
    // reset the default date each time the modal is reopened — otherwise a
    // long-lived panel would keep a stale (frozen) initial date.
    useEffect(() => {
        if (show) {
            setDate(getInitialDate());
        }
    }, [show, getInitialDate]);

    return (
        <PlainModal show={show} onHide={onCancel} size="md">
            <StandardModalHeader>{title}</StandardModalHeader>
            <VStack gap={16}>
                {description && (
                    <p className={styles.description}>{description}</p>
                )}
                {question && <div className="text-gray-500">{question}</div>}
                <Field
                    label={dateFieldLabel ?? dateLabel ?? ""}
                    control="button"
                    invalid={dateError !== undefined}
                    className={styles.dateField}
                >
                    <Field.Control
                        render={(controlProps) => (
                            <DatePicker
                                mode="day"
                                variant="panel-input"
                                clearable={false}
                                minDate={effectiveMinDate}
                                value={date}
                                onValueChange={(value) => {
                                    if (value) {
                                        setDate(value);
                                    }
                                }}
                                placeholder="Select date"
                                buttonProps={{
                                    variant: "primary",
                                    size: "xl",
                                }}
                                fieldControlProps={pickFieldControlAriaProps(
                                    controlProps,
                                )}
                            />
                        )}
                    />
                </Field>
            </VStack>
            <HStack gap={12} justify="end">
                <Button
                    type="button"
                    variant="secondary"
                    onClick={onCancel}
                    size="xl"
                    disabled={isProcessing}
                >
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    type="button"
                    size="xl"
                    disabled={
                        isProcessing || !isLockResolved || Boolean(dateError)
                    }
                    onClick={() => onConfirm(date)}
                >
                    {confirmLabel}
                </Button>
            </HStack>
        </PlainModal>
    );
};
